import { Box, Stack, ThemeSpaceVariable } from "@caspeco/casper-ui-library";
import { useFilteredAvailableTimes } from "api/api-hooks/use-available-times";
import { useBookingMenuRespond } from "api/api-hooks/use-booking-menu-respond";
import { useCancelBooking } from "api/api-hooks/use-cancel-booking";
import { useCreateBooking } from "api/api-hooks/use-create-booking";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import PreorderBody from "components/selection-components/preorder/preorder-body";
import PreorderContinueButton from "components/selection-components/preorder/preorder-continue-button";
import { PreorderContext } from "context/preorder-context";
import { useBookingStateContext } from "hooks/use-booking-state";
import BookingPreorderSkeleton from "pages/booking-preorder/booking-preorder-skeleton";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Routes } from "routes";
import { BookingMenu, MenuStatus } from "types/menu";

const BookingPreorderView = () => {
    const navigate = useNavigate();
    const {
        currentQuantities,
        setCurrentQuantities,
        currentTotalQuantity,
        setSelectedArticleHasMandatoryMenu,
    } = useContext(PreorderContext) || {};
    const { data: reservation, isPending } = useWebBooking();
    const createBooking = useCreateBooking();
    const state = useBookingStateContext();
    const [rebookBookingLoading, setRebookBookingLoading] = useState(false);

    const { date } = state;
    const filteredAvailableTimes = useFilteredAvailableTimes(
        date ?? reservation?.start.clone().startOf("day")
    );
    const ruleId = reservation?.webBookingRules?.at(0)?.id;
    const section = filteredAvailableTimes.find((timeSection) =>
        timeSection.timeSets.some((timeSet) => timeSet.id === ruleId)
    );

    const updateBookingMenu = useBookingMenuRespond();
    const cancelBooking = useCancelBooking();
    const { bookingId } = useParams();
    const [blockInitialQuantities, setBlockInitialMenuQuantities] = useState(false);
    const firstRender = useRef(true);
    const hasAlreadyResponded = reservation?.menus?.some(
        (menu) => menu.status === MenuStatus.Confirmed
    );
    useEffect(() => {
        if (!firstRender.current) return;
        firstRender.current = false;

        // When a user steps back to this view, we want to cancel the previous booking since the menu is already confirmed
        // then rebook it with the the same details.
        // This is a workaround for the fact that we don't have a way to update the booking menu in backend
        // TODO: Implement a way to update the booking menu without canceling and rebooking
        const handleCancelAndRebookBooking = async () => {
            setRebookBookingLoading(true);
            const timesToBook = reservation?.getBookTimes();
            const previousQuantites = new Map<number, number>();

            const menu = reservation?.menus.at(0);
            menu?.groups?.forEach((group) => {
                group.items?.forEach((item) => {
                    previousQuantites.set(item.articleID, item.quantity);
                });
            });

            cancelBooking.mutate({});
            const stateForReservation = { ...state, times: timesToBook };
            const newBooking = await createBooking.mutateAsync({ state: stateForReservation });
            const parts = location.pathname.split("/");
            parts[parts.length - 1] = newBooking.guid;
            const newPath = parts.join("/");
            await navigate(`${newPath}${location.search}`);

            const initialQuantities =
                newBooking.menus
                    .at(0)
                    ?.groups?.map((group) =>
                        (group.items ?? []).map(
                            (item) => previousQuantites.get(item.articleID) ?? item.quantity
                        )
                    ) ?? [];

            setCurrentQuantities?.(initialQuantities);
            setRebookBookingLoading(false);
        };
        if (hasAlreadyResponded) {
            setBlockInitialMenuQuantities(true);
            void handleCancelAndRebookBooking();
        }
    }, [
        bookingId,
        cancelBooking,
        createBooking,
        navigate,
        reservation,
        setSelectedArticleHasMandatoryMenu,
        setCurrentQuantities,
        state,
        hasAlreadyResponded,
    ]);

    useEffect(() => {
        if (reservation) {
            setSelectedArticleHasMandatoryMenu?.(
                Boolean(reservation.webBookingRules?.at(0)?.requireMenuSelection)
            );
        }
    }, [reservation, setSelectedArticleHasMandatoryMenu]);

    const onPreorderContinue = async () => {
        if (currentQuantities && currentTotalQuantity && currentTotalQuantity > 0) {
            const currentMenu = reservation?.menus?.find((menu) => menu.status === MenuStatus.Sent);
            if (!currentMenu) return;
            const bookingMenuWithChoices: BookingMenu = {
                ...currentMenu,
                groups: currentMenu?.groups?.map((group, groupIndex) => ({
                    ...group,
                    items: group.items?.map((item, itemIndex) => ({
                        ...item,
                        quantity: currentQuantities[groupIndex][itemIndex],
                    })),
                })),
            };
            await updateBookingMenu.mutateAsync(bookingMenuWithChoices);
        }

        const path = `${location.pathname.replace(Routes.PREORDER, Routes.FINALIZE)}${location.search}`;
        return navigate(path);
    };

    return (
        <Stack>
            <Box maxW={["100%", "560px"]} width="100vw">
                {isPending || rebookBookingLoading ? (
                    <BookingPreorderSkeleton />
                ) : (
                    <PreorderBody
                        key={"wizardbody-1"}
                        preorderHeaderName={section?.name ?? ""}
                        blockInitialQuantities={blockInitialQuantities}
                    />
                )}
            </Box>
            <Box px={ThemeSpaceVariable.Medium} pt={ThemeSpaceVariable.Medium}>
                <PreorderContinueButton onContinue={onPreorderContinue} />
            </Box>
        </Stack>
    );
};

export default BookingPreorderView;
